import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoAddCircle } from "react-icons/io5";
import {
  MdAccessTime,
  MdCalendarToday,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdDelete,
  MdExpandMore,
  MdSearch,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { toast } from "react-toastify";
import server from "../../api/server";
import UnauthorizedPage from "../layouts/UnauthorizedPage";
import {
  calculateReminderReschedule,
  calculateReminderRescheduleToDate,
} from "./reminderScheduling";
import "./RemindersLayout.css";

const PAST_RANGES = [
  { key: "yesterday", label: "Yesterday" },
  { key: "lastWeek", label: "Last Week" },
  { key: "last30", label: "Last 30 days" },
  { key: "last90", label: "Last 90 days" },
];

const UPCOMING_RANGES = [
  { key: "today", label: "Today" },
  { key: "thisWeek", label: "This Week" },
  { key: "next30", label: "Next 30 days" },
  { key: "next60", label: "Next 60 days" },
];

const QUICK_SCHEDULE_OPTIONS = ["0", "1", "2", "3", "4", "5", "6", "7"];

const EXTENDED_SCHEDULE_OPTIONS = [
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
  { value: "custom", label: "Custom days" },
  { value: "date", label: "Specific date" },
];

const DEFAULT_REMINDER_EMAILS = [
  "akhil@anthropod.in",
  "vishal@anthropod.in",
  "punit@anthropod.in",
];

const buildEditMailingList = (mailTo) => {
  const selectedRecipients = (Array.isArray(mailTo) ? mailTo : [mailTo])
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const selectedRecipientKeys = new Set(
    selectedRecipients.map((value) => value.toLowerCase())
  );
  const recipientOptions = [...DEFAULT_REMINDER_EMAILS];

  selectedRecipients.forEach((recipient) => {
    if (
      !recipientOptions.some(
        (option) => option.toLowerCase() === recipient.toLowerCase()
      )
    ) {
      recipientOptions.push(recipient);
    }
  });

  return recipientOptions.map((value) => ({
    value,
    checked: selectedRecipientKeys.has(value.toLowerCase()),
  }));
};

const startOfDay = (date) => {
  const nextDate = new Date(date);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
};

const endOfDay = (date) => {
  const nextDate = new Date(date);
  nextDate.setHours(23, 59, 59, 999);
  return nextDate;
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const startOfWeek = (date) => {
  const weekStart = startOfDay(date);
  const daysSinceMonday = (weekStart.getDay() + 6) % 7;
  weekStart.setDate(weekStart.getDate() - daysSinceMonday);
  return weekStart;
};

const parseDateValue = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameCalendarDay = (first, second) =>
  first?.getFullYear() === second?.getFullYear() &&
  first?.getMonth() === second?.getMonth() &&
  first?.getDate() === second?.getDate();

const ReminderDatePicker = ({
  id,
  value,
  onChange,
  align = "left",
  minDate = "",
}) => {
  const rootRef = useRef(null);
  const selectedDate = parseDateValue(value);
  const earliestDate = parseDateValue(minDate);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(
    () => selectedDate || new Date()
  );

  useEffect(() => {
    const nextSelectedDate = parseDateValue(value);
    if (nextSelectedDate) setVisibleMonth(nextSelectedDate);
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const today = new Date();
  const monthStart = new Date(
    visibleMonth.getFullYear(),
    visibleMonth.getMonth(),
    1
  );
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const calendarDays = Array.from({ length: 42 }, (_, index) =>
    addDays(gridStart, index)
  );
  const monthLabel = visibleMonth.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
  const displayValue = selectedDate
    ? selectedDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Select date";

  const selectDate = (date) => {
    if (earliestDate && startOfDay(date) < startOfDay(earliestDate)) return;
    onChange(formatDateValue(date));
    setVisibleMonth(date);
    setIsOpen(false);
  };

  const changeMonth = (offset) => {
    setVisibleMonth(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + offset, 1)
    );
  };

  return (
    <div
      ref={rootRef}
      className={`reminder-date-picker reminder-date-picker-align-${align}`}
    >
      <button
        id={id}
        type="button"
        className="reminder-date-trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <MdCalendarToday aria-hidden="true" />
        <span className={selectedDate ? "" : "reminder-date-placeholder"}>
          {displayValue}
        </span>
        <MdExpandMore aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="reminder-calendar-popover"
          role="dialog"
          aria-label="Choose a date"
        >
          <div className="reminder-calendar-header">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => changeMonth(-1)}
            >
              <MdChevronLeft aria-hidden="true" />
            </button>
            <strong aria-live="polite">{monthLabel}</strong>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => changeMonth(1)}
            >
              <MdChevronRight aria-hidden="true" />
            </button>
          </div>

          <div className="reminder-calendar-weekdays" aria-hidden="true">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <span key={`${day}-${index}`}>{day}</span>
            ))}
          </div>

          <div className="reminder-calendar-grid">
            {calendarDays.map((date) => {
              const isCurrentMonth =
                date.getMonth() === visibleMonth.getMonth();
              const isSelected = isSameCalendarDay(date, selectedDate);
              const isToday = isSameCalendarDay(date, today);
              const isBeforeEarliestDate =
                earliestDate && startOfDay(date) < startOfDay(earliestDate);
              return (
                <button
                  key={formatDateValue(date)}
                  type="button"
                  className={`${
                    isCurrentMonth ? "" : "reminder-calendar-day-muted "
                  }${isToday ? "reminder-calendar-day-today " : ""}${
                    isSelected ? "reminder-calendar-day-selected" : ""
                  }`}
                  aria-label={date.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                  aria-pressed={isSelected}
                  disabled={isBeforeEarliestDate}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="reminder-calendar-footer">
            <button
              type="button"
              disabled={
                earliestDate && startOfDay(today) < startOfDay(earliestDate)
              }
              onClick={() => selectDate(today)}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const getTimeParts = (value) => {
  const match = /^(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return null;
  const hour24 = Number(match[1]);
  const minute = Number(match[2]);
  if (hour24 > 23 || minute > 59) return null;
  return {
    hour: hour24 % 12 || 12,
    minute,
    period: hour24 >= 12 ? "PM" : "AM",
  };
};

const getCurrentTimeParts = () => {
  const now = new Date();
  const hour24 = now.getHours();
  return {
    hour: hour24 % 12 || 12,
    minute: now.getMinutes(),
    period: hour24 >= 12 ? "PM" : "AM",
  };
};

const formatTimeValue = ({ hour, minute, period }) => {
  const hour24 = (hour % 12) + (period === "PM" ? 12 : 0);
  return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(
    2,
    "0"
  )}`;
};

const formatTimeDisplay = (timeParts) =>
  `${String(timeParts.hour).padStart(2, "0")}:${String(
    timeParts.minute
  ).padStart(2, "0")} ${timeParts.period}`;

const ReminderTimePicker = ({ id, value, onChange }) => {
  const rootRef = useRef(null);
  const selectedHourRef = useRef(null);
  const selectedMinuteRef = useRef(null);
  const selectedTime = getTimeParts(value);
  const [isOpen, setIsOpen] = useState(false);
  const [draftTime, setDraftTime] = useState(
    () => selectedTime || getCurrentTimeParts()
  );

  useEffect(() => {
    if (!isOpen) {
      setDraftTime(getTimeParts(value) || getCurrentTimeParts());
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      selectedHourRef.current?.scrollIntoView({ block: "center" });
      selectedMinuteRef.current?.scrollIntoView({ block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [draftTime.hour, draftTime.minute, isOpen]);

  const applyTime = (timeParts) => {
    onChange(formatTimeValue(timeParts));
    setDraftTime(timeParts);
    setIsOpen(false);
  };

  const displayValue = selectedTime
    ? formatTimeDisplay(selectedTime)
    : "Select time";

  return (
    <div ref={rootRef} className="reminder-time-picker">
      <button
        id={id}
        type="button"
        className="reminder-time-trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={`Choose time, ${displayValue}`}
        onClick={() => setIsOpen((current) => !current)}
      >
        <MdAccessTime aria-hidden="true" />
        <span className={selectedTime ? "" : "reminder-time-placeholder"}>
          {displayValue}
        </span>
        <MdExpandMore aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="reminder-time-popover"
          role="dialog"
          aria-label="Choose a time"
        >
          <div className="reminder-time-header">
            <MdAccessTime aria-hidden="true" />
            <strong>{formatTimeDisplay(draftTime)}</strong>
          </div>

          <div className="reminder-time-columns">
            <div className="reminder-time-option-group">
              <span>Hour</span>
              <div
                className="reminder-time-option-list"
                role="group"
                aria-label="Hour"
              >
                {Array.from({ length: 12 }, (_, index) => index + 1).map(
                  (hour) => {
                    const isSelected = draftTime.hour === hour;
                    return (
                      <button
                        key={hour}
                        ref={isSelected ? selectedHourRef : undefined}
                        type="button"
                        aria-label={`${hour} hour`}
                        aria-pressed={isSelected}
                        className={isSelected ? "reminder-time-selected" : ""}
                        onClick={() =>
                          setDraftTime((current) => ({ ...current, hour }))
                        }
                      >
                        {String(hour).padStart(2, "0")}
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            <div className="reminder-time-option-group">
              <span>Minute</span>
              <div
                className="reminder-time-option-list"
                role="group"
                aria-label="Minute"
              >
                {Array.from({ length: 60 }, (_, minute) => minute).map(
                  (minute) => {
                    const isSelected = draftTime.minute === minute;
                    return (
                      <button
                        key={minute}
                        ref={isSelected ? selectedMinuteRef : undefined}
                        type="button"
                        aria-label={`${minute} minutes`}
                        aria-pressed={isSelected}
                        className={isSelected ? "reminder-time-selected" : ""}
                        onClick={() =>
                          setDraftTime((current) => ({ ...current, minute }))
                        }
                      >
                        {String(minute).padStart(2, "0")}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </div>

          <div
            className="reminder-time-period"
            role="group"
            aria-label="AM or PM"
          >
            {["AM", "PM"].map((period) => (
              <button
                key={period}
                type="button"
                aria-pressed={draftTime.period === period}
                className={
                  draftTime.period === period ? "reminder-time-selected" : ""
                }
                onClick={() =>
                  setDraftTime((current) => ({ ...current, period }))
                }
              >
                {period}
              </button>
            ))}
          </div>

          <div className="reminder-time-footer">
            <div>
              <button
                type="button"
                onClick={() => applyTime(getCurrentTimeParts())}
              >
                Now
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
              >
                Clear
              </button>
            </div>
            <button
              type="button"
              className="reminder-time-apply"
              onClick={() => applyTime(draftTime)}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ReminderSelectAll = ({
  reminders,
  selectedReminderIds,
  onToggle,
  listLabel,
}) => {
  const inputRef = useRef(null);
  const visibleIds = Array.from(
    new Set(
      reminders
        .map((reminder) => reminder.id)
        .filter((id) => id !== undefined && id !== null)
        .map(String)
    )
  );
  const selectedVisibleCount = visibleIds.filter(
    (id) => selectedReminderIds[id]
  ).length;
  const allVisibleSelected =
    visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const partiallySelected =
    selectedVisibleCount > 0 && !allVisibleSelected;
  const actionLabel = allVisibleSelected ? "Deselect all" : "Select all";

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = partiallySelected;
    }
  }, [partiallySelected]);

  return (
    <label
      className={`reminders-select-all${
        visibleIds.length === 0 ? " reminders-select-all-disabled" : ""
      }`}
    >
      <input
        ref={inputRef}
        type="checkbox"
        checked={allVisibleSelected}
        disabled={visibleIds.length === 0}
        aria-label={`${actionLabel} ${listLabel} reminders`}
        onChange={() => onToggle(visibleIds, !allVisibleSelected)}
      />
      <span>{actionLabel}</span>
    </label>
  );
};

const RemindersLayout = () => {
  const getTomorrowDateTimeAt8String = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(8, 0, 0, 0);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formatEpochForDateTimeLocal = (epochSeconds) => {
    const date = new Date(epochSeconds * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [reminderTitle, setReminderTitle] = useState("");
  const [dateTime, setDateTime] = useState(getTomorrowDateTimeAt8String());
  const [mailingList, setMailingList] = useState(() =>
    DEFAULT_REMINDER_EMAILS.map((value) => ({
      value,
      checked: value === "punit@anthropod.in",
    }))
  );
  const [editMailingList, setEditMailingList] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [reminderRemarks, setReminderRemarks] = useState("");
  const [allReminders, setAllReminders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [toEditData, setToEditData] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [pastRange, setPastRange] = useState("last30");
  const [upcomingRange, setUpcomingRange] = useState("today");
  const [pastSearch, setPastSearch] = useState("");
  const [upcomingSearch, setUpcomingSearch] = useState("");
  const [customSearch, setCustomSearch] = useState("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [customReminders, setCustomReminders] = useState([]);
  const [selectedReminderIds, setSelectedReminderIds] = useState({});
  const [showBulkScheduleModal, setShowBulkScheduleModal] = useState(false);
  const [bulkScheduleAfter, setBulkScheduleAfter] = useState("1");
  const [customScheduleDays, setCustomScheduleDays] = useState("");
  const [specificScheduleDate, setSpecificScheduleDate] = useState("");
  const [role] = useState(() => {
    const userCookie = getCookie("dashboard_login_user_creds");
    if (userCookie) {
      const userData = JSON.parse(userCookie);
      return userData.role;
    }
    return "admin";
  });

  function getCookie(name) {
    function escape(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }
    const match = document.cookie.match(
      RegExp(`(?:^|;\\s*)${escape(name)}=([^;]*)`)
    );
    return match ? match[1] : null;
  }

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleArrEmail = () => {
    if (!newEmail || !isValidEmail(newEmail)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setMailingList((prev) => [...prev, { value: newEmail, checked: true }]);
    setNewEmail("");
  };

  const generateRandomID = () => Math.random().toString(36).substr(2, 9);

  const handleCheckboxChange = (index) => {
    setMailingList((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const handleEditCheckboxChange = (index) => {
    setEditMailingList((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, checked: !item.checked } : item
      )
    );
  };

  const resetMailingListDefaults = () => {
    setMailingList((prev) =>
      prev.map((item) => ({
        ...item,
        checked: item.value === "punit@anthropod.in",
      }))
    );
  };

  const getReminders = useCallback(async () => {
    try {
      const startDate = startOfDay(addDays(new Date(), -90));
      const endDate = endOfDay(addDays(new Date(), 60));
      const startEpoch = Math.floor(startDate.getTime() / 1000);
      const endEpoch = Math.floor(endDate.getTime() / 1000);

      const res = await server.getReminders(startEpoch, endEpoch);
      setAllReminders(res?.resData.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch reminders.");
    }
  }, []);

  const getCustomReminders = useCallback(async () => {
    if (!customStartDate || !customEndDate) {
      setCustomReminders([]);
      return;
    }

    const parseDateInput = (value) => {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    };
    const startDate = startOfDay(parseDateInput(customStartDate));
    const endDate = endOfDay(parseDateInput(customEndDate));

    if (
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime()) ||
      startDate > endDate
    ) {
      setCustomReminders([]);
      return;
    }

    try {
      const res = await server.getReminders(
        Math.floor(startDate.getTime() / 1000),
        Math.floor(endDate.getTime() / 1000)
      );
      if (res.errorResponse?.error) {
        setCustomReminders([]);
        toast.error(res.errorResponse.error);
        return;
      }
      const reminders = res?.resData.data || [];
      setCustomReminders(
        [...reminders].sort(
          (first, second) => Number(first.time) - Number(second.time)
        )
      );
    } catch (error) {
      console.error(error);
      setCustomReminders([]);
      toast.error("Failed to fetch reminders for the custom date range.");
    }
  }, [customEndDate, customStartDate]);

  const handleAddReminder = async () => {
    if (!reminderTitle || !dateTime) {
      toast.error("Please fill all required fields.");
      return;
    }

    const epochTime = new Date(dateTime).getTime() / 1000;
    if (Number.isNaN(epochTime)) {
      toast.error("Invalid date and time.");
      return;
    }

    const selectedEmails = mailingList
      .filter((item) => item.checked)
      .map((item) => item.value);

    const payload = [
      {
        id: generateRandomID(),
        reminder_title: reminderTitle,
        time: epochTime,
        mail_to: selectedEmails,
        reminder_description: reminderRemarks,
        notification_time: epochTime,
      },
    ];

    try {
      const res = await server.postReminder(payload);
      if (res.errorResponse?.error) {
        toast.error(res.errorResponse.error);
      } else {
        toast.success("Reminder Created Successfully!");
        setReminderTitle("");
        setDateTime(getTomorrowDateTimeAt8String());
        resetMailingListDefaults();
        setReminderRemarks("");
        getReminders();
        getCustomReminders();
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred.");
    }
  };

  const filterReminders = (range) => {
    const todayStart = startOfDay(new Date());
    const todayEnd = endOfDay(todayStart);
    const thisWeekStart = startOfWeek(todayStart);

    const remindersInRange = (start, end, direction = "asc") =>
      allReminders
        .filter((reminder) => {
          const reminderDate = new Date(Number(reminder.time) * 1000);
          return reminderDate >= start && reminderDate <= end;
        })
        .sort((first, second) =>
          direction === "desc"
            ? Number(second.time) - Number(first.time)
            : Number(first.time) - Number(second.time)
        );

    switch (range) {
      case "today":
        return remindersInRange(todayStart, todayEnd);
      case "thisWeek":
        return remindersInRange(
          todayStart,
          endOfDay(addDays(thisWeekStart, 6))
        );
      case "next30":
        return remindersInRange(
          startOfDay(addDays(todayStart, 1)),
          endOfDay(addDays(todayStart, 30))
        );
      case "next60":
        return remindersInRange(
          startOfDay(addDays(todayStart, 1)),
          endOfDay(addDays(todayStart, 60))
        );
      case "yesterday":
        return remindersInRange(
          startOfDay(addDays(todayStart, -1)),
          endOfDay(addDays(todayStart, -1)),
          "desc"
        );
      case "lastWeek":
        return remindersInRange(
          startOfDay(addDays(thisWeekStart, -7)),
          new Date(thisWeekStart.getTime() - 1),
          "desc"
        );
      case "last90":
        return remindersInRange(
          startOfDay(addDays(todayStart, -90)),
          new Date(todayStart.getTime() - 1),
          "desc"
        );
      case "last30":
        return remindersInRange(
          startOfDay(addDays(todayStart, -30)),
          new Date(todayStart.getTime() - 1),
          "desc"
        );
      default:
        return [];
    }
  };

  const toggleReminderSelection = (id) => {
    setSelectedReminderIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleVisibleReminderSelection = (visibleIds, shouldSelect) => {
    setSelectedReminderIds((prev) => {
      const nextSelection = { ...prev };
      visibleIds.forEach((id) => {
        if (shouldSelect) {
          nextSelection[id] = true;
        } else {
          delete nextSelection[id];
        }
      });
      return nextSelection;
    });
  };

  const clearSelectedReminders = () => setSelectedReminderIds({});

  const selectedReminderCount = Object.values(selectedReminderIds).filter(
    Boolean
  ).length;

  const handleBulkSchedule = async () => {
    if (bulkScheduleAfter === "custom" && customScheduleDays.trim() === "") {
      toast.error("Please enter the number of days.");
      return;
    }

    const isSpecificDateSchedule = bulkScheduleAfter === "date";
    const targetScheduleDate = parseDateValue(specificScheduleDate);
    if (isSpecificDateSchedule && !targetScheduleDate) {
      toast.error("Please select a date.");
      return;
    }

    const days = isSpecificDateSchedule
      ? null
      : bulkScheduleAfter === "custom"
      ? Number(customScheduleDays)
      : Number(bulkScheduleAfter);

    if (!isSpecificDateSchedule && (Number.isNaN(days) || days < 0)) {
      toast.error("Please enter a valid number of days.");
      return;
    }

    const selectedIds = Object.keys(selectedReminderIds).filter(
      (id) => selectedReminderIds[id]
    );
    if (!selectedIds.length) {
      toast.error("Please select at least one reminder.");
      return;
    }

    const schedulingNow = new Date();
    const remindersById = new Map(
      [...allReminders, ...customReminders].map((reminder) => [
        String(reminder.id),
        reminder,
      ])
    );
    const updates = selectedIds
      .map((id) => remindersById.get(id))
      .filter(Boolean)
      .map((reminder) => {
        const { updatedEpoch } = isSpecificDateSchedule
          ? calculateReminderRescheduleToDate(
              reminder.time,
              specificScheduleDate,
              schedulingNow
            )
          : calculateReminderReschedule(
              reminder.time,
              days,
              schedulingNow
            );
        return {
          id: reminder.id,
          reminder_title: reminder.reminder_title,
          time: updatedEpoch,
          mail_to: reminder.mail_to || [],
          reminder_description: reminder.reminder_description,
          notification_time: updatedEpoch,
          reset_sent: true,
        };
      });

    const isTodaySchedule =
      bulkScheduleAfter === "0" ||
      (isSpecificDateSchedule &&
        isSameCalendarDay(targetScheduleDate, schedulingNow));
    if (
      isTodaySchedule &&
      updates.some((reminder) => reminder.time <= schedulingNow.getTime() / 1000)
    ) {
      toast.error("Today no longer has an available time. Choose a future date.");
      return;
    }

    if (
      isSpecificDateSchedule &&
      updates.some((reminder) => reminder.time <= schedulingNow.getTime() / 1000)
    ) {
      toast.error(
        "The selected date would place one or more reminders in the past. Choose a future date."
      );
      return;
    }

    try {
      const res = await server.postReminder(updates);
      if (res.errorResponse?.error) {
        toast.error(res.errorResponse.error);
      } else {
        toast.success("Selected reminders scheduled successfully!");
        setShowBulkScheduleModal(false);
        clearSelectedReminders();
        setBulkScheduleAfter("1");
        setCustomScheduleDays("");
        setSpecificScheduleDate("");
        getReminders();
        getCustomReminders();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update selected reminders.");
    }
  };

  const handleBulkDelete = async () => {
    const selectedIds = Object.keys(selectedReminderIds).filter(
      (id) => selectedReminderIds[id]
    );
    if (!selectedIds.length) {
      toast.error("Please select at least one reminder.");
      return;
    }

    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) => server.deleteReminder(id))
      );
      const successCount = results.filter(
        (result) =>
          result.status === "fulfilled" && result.value?.status === 200
      ).length;

      if (successCount > 0) {
        toast.success(`${successCount} reminder(s) deleted successfully!`);
        clearSelectedReminders();
        getReminders();
        getCustomReminders();
      }

      if (successCount !== selectedIds.length) {
        toast.error("Some reminders could not be deleted.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete selected reminders.");
    }
  };

  const deleteReminder = async () => {
    try {
      setShowModal(false);
      const res = await server.deleteReminder(selectedId);
      if (res.status === 200) {
        toast.success("Reminder Deleted Successfully!");
      }
    } catch (error) {
      console.error(error);
    }
    setAllReminders((prev) =>
      prev.filter((reminder) => reminder.id !== selectedId)
    );
    setCustomReminders((prev) =>
      prev.filter((reminder) => reminder.id !== selectedId)
    );
    setSelectedId(null);
  };

  const getFormattedTime = (reminderDate) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return reminderDate
      .toLocaleString("en-GB", options)
      .replace(",", " |")
      .toUpperCase();
  };

  const searchReminders = (reminders, query) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return reminders;

    return reminders.filter((reminder) => {
      const recipients = Array.isArray(reminder.mail_to)
        ? reminder.mail_to
        : [reminder.mail_to];
      return [
        reminder.reminder_title,
        reminder.reminder_description,
        ...recipients,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(normalizedQuery)
      );
    });
  };

  const handleDelete = (id) => {
    setShowModal(true);
    setSelectedId(id);
  };

  const handleOpenEdit = (reminder, uniqueIdx) => {
    if (selectedIdx === uniqueIdx) {
      setSelectedIdx(null);
      setToEditData(null);
      return;
    }

    const formattedTime = formatEpochForDateTimeLocal(reminder.time);
    setToEditData({
      id: reminder.id,
      reminderTitle: reminder.reminder_title,
      time: formattedTime,
      originalTime: formattedTime,
      mailingList: reminder.mail_to,
      reminderRemarks: reminder.reminder_description,
    });
    setSelectedIdx(uniqueIdx);
  };

  const editReminder = async () => {
    if (!toEditData.reminderTitle || !toEditData.time) {
      toast.error("Please fill all required fields.");
      return;
    }

    const epochTime = new Date(toEditData.time).getTime() / 1000;
    if (Number.isNaN(epochTime)) {
      toast.error("Invalid date and time.");
      return;
    }

    const updatedReminder = {
      id: toEditData.id,
      reminder_title: toEditData.reminderTitle,
      time: epochTime,
      mail_to: editMailingList
        .filter((item) => item.checked)
        .map((item) => item.value),
      reminder_description: toEditData.reminderRemarks,
      notification_time: epochTime,
      reset_sent: toEditData.time !== toEditData.originalTime,
    };

    try {
      const res = await server.postReminder([updatedReminder]);
      if (res.errorResponse?.error) {
        toast.error(res.errorResponse.error);
      } else {
        toast.success("Reminder Updated Successfully!");
        setShowEditModal(false);
        setToEditData(null);
        setEditMailingList([]);
        getReminders();
        getCustomReminders();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update the reminder.");
    }
  };

  useEffect(() => {
    getReminders();
  }, [getReminders]);

  useEffect(() => {
    getCustomReminders();
  }, [getCustomReminders]);

  const pastRangeLabel =
    PAST_RANGES.find((range) => range.key === pastRange)?.label || "Past";
  const upcomingRangeLabel =
    UPCOMING_RANGES.find((range) => range.key === upcomingRange)?.label ||
    "Upcoming";
  const pastReminders = searchReminders(
    filterReminders(pastRange),
    pastSearch
  );
  const upcomingReminders = searchReminders(
    filterReminders(upcomingRange),
    upcomingSearch
  );
  const visibleCustomReminders = searchReminders(
    customReminders,
    customSearch
  );

  const renderReminderList = (reminders, listKey) => {
    if (!reminders.length) {
      return (
        <div className="reminders-empty-state">
          <span className="reminders-empty-icon" aria-hidden="true">
            ✓
          </span>
          <strong>No reminders here</strong>
          <span>Try another time range.</span>
        </div>
      );
    }

    return reminders.map((reminder, index) => {
      const reminderDate = new Date(Number(reminder.time) * 1000);
      const formattedTime = getFormattedTime(reminderDate);
      const uniqueIdx = `${listKey}-${index}`;
      const isExpanded =
        selectedIdx === uniqueIdx && toEditData?.id === reminder.id;
      const reminderEmails = Array.isArray(reminder.mail_to)
        ? reminder.mail_to
        : [];

      return (
        <div
          key={`${listKey}-${reminder.id}`}
          className={`reminder-row${
            selectedReminderIds[reminder.id] ? " reminder-row-selected" : ""
          }`}
        >
          <label className="reminder-select-control">
            <input
              type="checkbox"
              aria-label={`Select ${reminder.reminder_title}`}
              checked={!!selectedReminderIds[reminder.id]}
              onChange={() => toggleReminderSelection(reminder.id)}
            />
          </label>

          <article className="reminder-card">
            <div className="reminder-card-header">
              <button
                type="button"
                className="reminder-card-summary"
                aria-label={`${isExpanded ? "Hide" : "View"} details for ${
                  reminder.reminder_title
                }`}
                aria-expanded={isExpanded}
                onClick={() => handleOpenEdit(reminder, uniqueIdx)}
              >
                <span className="reminder-time">{formattedTime}</span>
                <span className="reminder-title-row">
                  <span
                    className={`reminder-title${
                      isExpanded ? " reminder-title-expanded" : ""
                    }`}
                  >
                    {reminder.reminder_title}
                  </span>
                </span>
              </button>

              <div className="reminder-card-quick-actions">
                <button
                  type="button"
                  className="reminder-details-eye"
                  aria-label={`${isExpanded ? "Hide" : "View"} details for ${
                    reminder.reminder_title
                  }`}
                  aria-expanded={isExpanded}
                  onClick={() => handleOpenEdit(reminder, uniqueIdx)}
                >
                  {isExpanded ? <MdVisibilityOff /> : <MdVisibility />}
                </button>
                <button
                  type="button"
                  className="reminder-delete-button"
                  aria-label={`Delete ${reminder.reminder_title}`}
                  onClick={() => handleDelete(reminder.id)}
                >
                  <MdDelete aria-hidden="true" />
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="reminder-details">
                <div className="reminder-detail-block">
                  <span className="reminder-detail-label">Remarks</span>
                  <p>{reminder.reminder_description || "No remarks added"}</p>
                </div>
                <div className="reminder-detail-block">
                  <span className="reminder-detail-label">Emails</span>
                  <p>
                    {reminderEmails.length
                      ? reminderEmails.join(", ")
                      : "No recipients added"}
                  </p>
                </div>
                <div className="reminder-card-actions">
                  <button
                    type="button"
                    className="reminder-button reminder-button-dark"
                    onClick={() => {
                      setEditMailingList(
                        buildEditMailingList(toEditData?.mailingList)
                      );
                      setSelectedIdx(null);
                      setShowEditModal(true);
                    }}
                  >
                    Edit reminder
                  </button>
                </div>
              </div>
            )}
          </article>
        </div>
      );
    });
  };

  const renderRangeButtons = (ranges, activeRange, setActiveRange, label) => (
    <div className="reminder-range-grid" role="group" aria-label={label}>
      {ranges.map((range) => (
        <button
          key={range.key}
          type="button"
          className={`reminder-range-button${
            activeRange === range.key ? " reminder-range-button-active" : ""
          }`}
          aria-pressed={activeRange === range.key}
          onClick={() => {
            setActiveRange(range.key);
            setSelectedIdx(null);
            setToEditData(null);
          }}
        >
          {range.label}
        </button>
      ))}
    </div>
  );

  const renderDateTimeSelector = (value, onChange, idPrefix) => {
    const [selectedDate = "", selectedTime = ""] = (value || "").split("T");
    const updatePart = (part, nextValue) => {
      const nextDate = part === "date" ? nextValue : selectedDate;
      const nextTime = part === "time" ? nextValue : selectedTime;
      onChange(nextDate || nextTime ? `${nextDate}T${nextTime}` : "");
    };

    return (
      <fieldset className="reminder-date-time-selector">
        <legend>Date and time</legend>
        <div className="reminder-date-time-grid">
          <div className="reminder-date-time-field">
            <ReminderDatePicker
              id={`${idPrefix}-date`}
              value={selectedDate}
              onChange={(nextDate) => updatePart("date", nextDate)}
            />
          </div>
          <div className="reminder-date-time-field">
            <ReminderTimePicker
              id={`${idPrefix}-time`}
              value={selectedTime}
              onChange={(nextTime) => updatePart("time", nextTime)}
            />
          </div>
        </div>
      </fieldset>
    );
  };

  return (
    <main className="reminders-page font-sans">
      <div className="reminders-shell">
        {selectedReminderCount > 0 && (
          <section className="reminders-bulk-toolbar" aria-label="Bulk actions">
            <div>
              <strong>{selectedReminderCount} selected</strong>
              <span>Apply an action to the selected reminders.</span>
            </div>
            <div className="reminders-bulk-actions">
              <button
                type="button"
                className="reminder-button reminder-button-secondary"
                onClick={clearSelectedReminders}
              >
                Clear
              </button>
              <button
                type="button"
                className="reminder-button reminder-button-danger"
                onClick={handleBulkDelete}
              >
                Delete selected
              </button>
              <button
                type="button"
                className="reminder-button reminder-button-primary"
                onClick={() => setShowBulkScheduleModal(true)}
              >
                Schedule selected
              </button>
            </div>
          </section>
        )}

        <div className="reminders-dashboard">
          <section className="reminders-panel reminders-list-panel">
            <div className="reminders-panel-heading">
              <h2>Past Reminders</h2>
              <p>Review and reschedule reminders that have already passed.</p>
            </div>
            {renderRangeButtons(
              PAST_RANGES,
              pastRange,
              setPastRange,
              "Past reminder period"
            )}
            <div className="reminders-list-heading">
              <label className="reminders-search">
                <MdSearch aria-hidden="true" />
                <input
                  type="search"
                  value={pastSearch}
                  onChange={(event) => setPastSearch(event.target.value)}
                  aria-label={`Search ${pastRangeLabel} reminders`}
                  placeholder={`Search ${pastRangeLabel} reminders`}
                />
              </label>
              <div className="reminders-list-actions">
                <ReminderSelectAll
                  reminders={pastReminders}
                  selectedReminderIds={selectedReminderIds}
                  onToggle={toggleVisibleReminderSelection}
                  listLabel={pastRangeLabel}
                />
                <span
                  className="reminders-list-count"
                  aria-label={`${pastReminders.length} reminders`}
                >
                  {pastReminders.length}
                </span>
              </div>
            </div>
            <div className="reminders-list">
              {renderReminderList(pastReminders, pastRange)}
            </div>
          </section>

          <section className="reminders-panel reminders-create-panel">
            <div className="reminders-panel-heading">
              <h2>Create Reminders</h2>
              <p>Add the details and choose who should receive the reminder.</p>
            </div>

            {role === "superadmin" ? (
              <form
                className="reminder-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleAddReminder();
                }}
              >
                <div className="reminder-form-group">
                  <label htmlFor="reminder-title">Reminder title</label>
                  <input
                    id="reminder-title"
                    type="text"
                    value={reminderTitle}
                    onChange={(event) => setReminderTitle(event.target.value)}
                    placeholder="Enter reminder title"
                  />
                </div>

                {renderDateTimeSelector(
                  dateTime,
                  setDateTime,
                  "create-reminder"
                )}

                <fieldset className="reminder-recipient-fieldset">
                  <legend>Mail to</legend>
                  <div className="reminder-mailing-list">
                    {mailingList.map((item, index) => (
                      <label key={item.value} className="reminder-email-option">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => handleCheckboxChange(index)}
                        />
                        <span>{item.value}</span>
                      </label>
                    ))}
                  </div>
                  <div className="reminder-add-email">
                    <input
                      type="text"
                      aria-label="Additional reminder email"
                      placeholder="Add another email"
                      value={newEmail}
                      onChange={(event) => setNewEmail(event.target.value)}
                    />
                    <button
                      type="button"
                      aria-label="Add email"
                      onClick={handleArrEmail}
                    >
                      <IoAddCircle aria-hidden="true" />
                    </button>
                  </div>
                </fieldset>

                <div className="reminder-form-group">
                  <label htmlFor="reminder-remarks">
                    Remarks <span>Optional</span>
                  </label>
                  <textarea
                    id="reminder-remarks"
                    value={reminderRemarks}
                    onChange={(event) => setReminderRemarks(event.target.value)}
                    placeholder="Add helpful context"
                    rows="4"
                  />
                </div>

                <button
                  type="submit"
                  className="reminder-button reminder-button-primary reminder-button-full"
                >
                  Add reminder
                </button>
              </form>
            ) : (
              <div className="reminders-unauthorized">
                <UnauthorizedPage />
              </div>
            )}
          </section>

          <section className="reminders-panel reminders-list-panel">
            <div className="reminders-panel-heading">
              <h2>Upcoming Reminders</h2>
              <p>See what needs attention today and in the weeks ahead.</p>
            </div>
            {renderRangeButtons(
              UPCOMING_RANGES,
              upcomingRange,
              setUpcomingRange,
              "Upcoming reminder period"
            )}
            <div className="reminders-list-heading">
              <label className="reminders-search">
                <MdSearch aria-hidden="true" />
                <input
                  type="search"
                  value={upcomingSearch}
                  onChange={(event) => setUpcomingSearch(event.target.value)}
                  aria-label={`Search ${upcomingRangeLabel} reminders`}
                  placeholder={`Search ${upcomingRangeLabel} reminders`}
                />
              </label>
              <div className="reminders-list-actions">
                <ReminderSelectAll
                  reminders={upcomingReminders}
                  selectedReminderIds={selectedReminderIds}
                  onToggle={toggleVisibleReminderSelection}
                  listLabel={upcomingRangeLabel}
                />
                <span
                  className="reminders-list-count"
                  aria-label={`${upcomingReminders.length} reminders`}
                >
                  {upcomingReminders.length}
                </span>
              </div>
            </div>
            <div className="reminders-list">
              {renderReminderList(upcomingReminders, upcomingRange)}
            </div>
          </section>
        </div>

        <section className="reminders-panel reminders-custom-panel">
          <div className="reminders-custom-header">
            <div className="reminders-panel-heading">
              <h2>Custom Date Range</h2>
            </div>
            <div className="reminders-custom-inputs">
              <div className="reminders-custom-date-field">
                <span>From</span>
                <ReminderDatePicker
                  id="custom-reminder-from"
                  value={customStartDate}
                  onChange={setCustomStartDate}
                />
              </div>
              <div className="reminders-custom-date-field">
                <span>To</span>
                <ReminderDatePicker
                  id="custom-reminder-to"
                  value={customEndDate}
                  onChange={setCustomEndDate}
                  align="right"
                />
              </div>
            </div>
          </div>
          <div className="reminders-list-heading">
            <label className="reminders-search">
              <MdSearch aria-hidden="true" />
              <input
                type="search"
                value={customSearch}
                onChange={(event) => setCustomSearch(event.target.value)}
                aria-label="Search custom date reminders"
                placeholder="Search custom date reminders"
              />
            </label>
            <div className="reminders-list-actions">
              <ReminderSelectAll
                reminders={visibleCustomReminders}
                selectedReminderIds={selectedReminderIds}
                onToggle={toggleVisibleReminderSelection}
                listLabel="custom date range"
              />
              <span
                className="reminders-list-count"
                aria-label={`${visibleCustomReminders.length} reminders`}
              >
                {visibleCustomReminders.length}
              </span>
            </div>
          </div>
          <div className="reminders-list reminders-custom-list">
            {renderReminderList(visibleCustomReminders, "custom")}
          </div>
        </section>
      </div>

      {showBulkScheduleModal && (
        <div className="reminder-modal-backdrop">
          <section
            className="reminder-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-reminders-title"
          >
            <button
              type="button"
              className="reminder-modal-close"
              aria-label="Close schedule dialog"
              onClick={() => {
                setShowBulkScheduleModal(false);
                setBulkScheduleAfter("1");
                setCustomScheduleDays("");
                setSpecificScheduleDate("");
              }}
            >
              <MdClose aria-hidden="true" />
            </button>
            <div className="reminder-modal-heading">
              <span className="reminders-panel-kicker">Bulk action</span>
              <h2 id="schedule-reminders-title">Schedule selected</h2>
            </div>
            <fieldset className="reminder-schedule-fieldset">
              <legend>
                Schedule after (from today for past reminders)
              </legend>
              <div className="reminder-schedule-options reminder-schedule-options-quick">
                {QUICK_SCHEDULE_OPTIONS.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`reminder-schedule-option${
                      bulkScheduleAfter === value
                        ? " reminder-schedule-option-active"
                        : ""
                    }`}
                    aria-pressed={bulkScheduleAfter === value}
                    onClick={() => setBulkScheduleAfter(value)}
                  >
                    {value === "0"
                      ? "Today"
                      : `${value} ${value === "1" ? "day" : "days"}`}
                  </button>
                ))}
              </div>
              <div className="reminder-schedule-options reminder-schedule-options-extended">
                {EXTENDED_SCHEDULE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`reminder-schedule-option${
                      bulkScheduleAfter === option.value
                        ? " reminder-schedule-option-active"
                        : ""
                    }`}
                    aria-pressed={bulkScheduleAfter === option.value}
                    onClick={() => setBulkScheduleAfter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>
            {bulkScheduleAfter === "custom" && (
              <div className="reminder-form-group">
                <label htmlFor="custom-schedule-days">Number of days</label>
                <input
                  id="custom-schedule-days"
                  type="number"
                  min="0"
                  value={customScheduleDays}
                  onChange={(event) => setCustomScheduleDays(event.target.value)}
                  placeholder="Enter days"
                />
              </div>
            )}
            {bulkScheduleAfter === "date" && (
              <div className="reminder-form-group">
                <label htmlFor="specific-schedule-date">Reschedule to</label>
                <ReminderDatePicker
                  id="specific-schedule-date"
                  value={specificScheduleDate}
                  onChange={setSpecificScheduleDate}
                  minDate={formatDateValue(new Date())}
                />
              </div>
            )}
            <p className="reminder-modal-note">
              {bulkScheduleAfter === "0"
                ? "Selected reminders will move to today. Times that have already passed will move to the next available time today."
                : bulkScheduleAfter === "date"
                ? "Selected reminders will move to the chosen date. If today is selected and a reminder time has passed, it will move to the next available time today."
                : "Past reminders will be scheduled from today's date. Existing reminder times will be preserved."}
            </p>
            <div className="reminder-modal-actions">
              <button
                type="button"
                className="reminder-button reminder-button-secondary"
                onClick={() => {
                  setShowBulkScheduleModal(false);
                  setBulkScheduleAfter("1");
                  setCustomScheduleDays("");
                  setSpecificScheduleDate("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="reminder-button reminder-button-primary"
                onClick={handleBulkSchedule}
              >
                Save schedule
              </button>
            </div>
          </section>
        </div>
      )}

      {showModal && (
        <div className="reminder-modal-backdrop">
          <section
            className="reminder-modal-card reminder-modal-card-small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-reminder-title"
          >
            <button
              type="button"
              className="reminder-modal-close"
              aria-label="Close delete dialog"
              onClick={() => {
                setShowModal(false);
                setSelectedId(null);
              }}
            >
              <MdClose aria-hidden="true" />
            </button>
            <div className="reminder-modal-heading">
              <span className="reminders-panel-kicker">Confirm action</span>
              <h2 id="delete-reminder-title">Delete this reminder?</h2>
              <p>This action cannot be undone.</p>
            </div>
            <div className="reminder-modal-actions">
              <button
                type="button"
                className="reminder-button reminder-button-secondary"
                onClick={() => {
                  setShowModal(false);
                  setSelectedId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="reminder-button reminder-button-danger"
                onClick={deleteReminder}
              >
                Delete reminder
              </button>
            </div>
          </section>
        </div>
      )}

      {showEditModal && (
        <div className="reminder-modal-backdrop">
          <section
            className="reminder-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-reminder-title"
          >
            <button
              type="button"
              className="reminder-modal-close"
              aria-label="Close edit dialog"
              onClick={() => {
                setShowEditModal(false);
                setToEditData(null);
                setEditMailingList([]);
              }}
            >
              <MdClose aria-hidden="true" />
            </button>
            <div className="reminder-modal-heading">
              <span className="reminders-panel-kicker">Reminder details</span>
              <h2 id="edit-reminder-title">Edit reminder</h2>
            </div>
            <div className="reminder-form-group">
              <label htmlFor="edit-reminder-name">Reminder title</label>
              <input
                id="edit-reminder-name"
                type="text"
                value={toEditData?.reminderTitle || ""}
                onChange={(event) =>
                  setToEditData((prev) => ({
                    ...prev,
                    reminderTitle: event.target.value,
                  }))
                }
                placeholder="Enter reminder title"
              />
            </div>
            {renderDateTimeSelector(
              toEditData?.time || "",
              (nextValue) =>
                setToEditData((prev) => ({
                  ...prev,
                  time: nextValue,
                })),
              "edit-reminder"
            )}
            <div className="reminder-form-group">
              <label htmlFor="edit-reminder-remarks">Remarks</label>
              <textarea
                id="edit-reminder-remarks"
                value={toEditData?.reminderRemarks || ""}
                onChange={(event) =>
                  setToEditData((prev) => ({
                    ...prev,
                    reminderRemarks: event.target.value,
                  }))
                }
                placeholder="Enter reminder remarks"
                rows="3"
              />
            </div>
            <fieldset className="reminder-recipient-fieldset">
              <legend>Mail to</legend>
              <div className="reminder-mailing-list">
                {editMailingList.map((item, index) => (
                  <label key={item.value} className="reminder-email-option">
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => handleEditCheckboxChange(index)}
                    />
                    <span>{item.value}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="reminder-modal-actions">
              <button
                type="button"
                className="reminder-button reminder-button-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setToEditData(null);
                  setEditMailingList([]);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="reminder-button reminder-button-primary"
                onClick={editReminder}
              >
                Save changes
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
};

export default RemindersLayout;
